/**
 * @fileoverview ソースのグループ化ロジック
 * @description 同じドキュメント+ページのソースを1つのグループにまとめる
 */

import type { ChatSource } from '@karosorabei/shared'

/**
 * グループ化された出典情報
 * @description 同じドキュメント+ページのソースをまとめた構造（新構造対応）
 */
export interface GroupedSource {
  /** グループキー（ドキュメント名 + ページ番号） */
  key: string
  /** ドキュメントタイトル（バージョン含む） */
  documentTitle: string
  /** 章テキスト（例: "第2章 無人航空機操縦者の心得"） */
  chapterText?: string
  /** 節テキスト（例: "2.1 操縦者の役割と責任"） */
  sectionText?: string
  /** 項テキスト（例: "2.1.1 操縦者としての自覚"） */
  subsectionText?: string
  /** ページ番号（文書ページ番号） */
  page?: number
  /** PDF物理ページ番号（PDF.jsやブラウザで開くときのページ番号） */
  actualPdfPage?: number
  /** グループ内の最高スコア */
  maxScore: number
  /** グループ内の引用リスト（スコア降順） */
  quotes: Array<{
    chunkId: string
    text: string
    score: number
    /** 章テキスト（例: "第2章 無人航空機操縦者の心得"） - 各引用ごとに異なる場合があるため */
    chapterText?: string
    /** 節テキスト（例: "2.1 操縦者の役割と責任"） */
    sectionText?: string
    /** 項テキスト（例: "2.1.1 操縦者としての自覚"） */
    subsectionText?: string
  }>
  /** 配布元PDF URL（タイトルクリックで開く） */
  sourceUrl?: string
}

/**
 * ソースをドキュメント+ページでグループ化する
 * @description
 * 同じドキュメント・同じページからの複数のソースを1つのグループにまとめる。
 * グループ内の引用はスコア降順でソートされ、グループ自体も最高スコア降順でソートされる。
 *
 * @param sources - グループ化するソースの配列
 * @returns グループ化されたソースの配列（最高スコア降順）
 *
 * @example
 * ```ts
 * const sources = [
 *   { chunkId: '1', text: 'テキスト1', score: 0.8, page: 13, metadata: { title: '教則' } },
 *   { chunkId: '2', text: 'テキスト2', score: 0.7, page: 13, metadata: { title: '教則' } },
 * ]
 * const grouped = groupSourcesByPage(sources)
 * // => 1つのグループ（page: 13, quotes: 2つ）
 * ```
 */
export function groupSourcesByPage(sources: ChatSource[]): GroupedSource[] {
  // 空配列の場合は早期リターン
  if (sources.length === 0) {
    return []
  }

  // グループ化用のMap（キー: "ドキュメントタイトル|ページ番号"）
  const groupMap = new Map<string, GroupedSource>()

  for (const source of sources) {
    // ドキュメントタイトルを取得（なければ「不明なドキュメント」）
    const documentTitle = source.metadata?.title ?? '不明なドキュメント'
    // ページ番号（undefinedの場合は 'undefined' 文字列をキーに含める）
    const pageKey = source.page?.toString() ?? 'undefined'
    // グループキーを生成
    const key = `${documentTitle}|${pageKey}`

    const existingGroup = groupMap.get(key)

    if (existingGroup) {
      // 既存グループに引用を追加（各引用ごとに階層情報を保持）
      const quote: GroupedSource['quotes'][0] = {
        chunkId: source.chunkId,
        text: source.text,
        score: source.score,
      }
      // 階層情報は各引用ごとに保持（同じページでも異なる章・節・項の場合があるため）
      if (source.metadata?.chapterText !== undefined) {
        quote.chapterText = source.metadata.chapterText
      }
      if (source.metadata?.sectionText !== undefined) {
        quote.sectionText = source.metadata.sectionText
      }
      if (source.metadata?.subsectionText !== undefined) {
        quote.subsectionText = source.metadata.subsectionText
      }
      existingGroup.quotes.push(quote)
      // 最高スコアを更新
      if (source.score > existingGroup.maxScore) {
        existingGroup.maxScore = source.score
        // 最高スコアの引用の階層情報を使用
        if (source.metadata?.chapterText !== undefined) {
          existingGroup.chapterText = source.metadata.chapterText
        }
        if (source.metadata?.sectionText !== undefined) {
          existingGroup.sectionText = source.metadata.sectionText
        }
        if (source.metadata?.subsectionText !== undefined) {
          existingGroup.subsectionText = source.metadata.subsectionText
        }
      }
    } else {
      // 新しいグループを作成（最初の引用にも階層情報を保持）
      const firstQuote: GroupedSource['quotes'][0] = {
        chunkId: source.chunkId,
        text: source.text,
        score: source.score,
      }
      // 階層情報は各引用ごとに保持
      if (source.metadata?.chapterText !== undefined) {
        firstQuote.chapterText = source.metadata.chapterText
      }
      if (source.metadata?.sectionText !== undefined) {
        firstQuote.sectionText = source.metadata.sectionText
      }
      if (source.metadata?.subsectionText !== undefined) {
        firstQuote.subsectionText = source.metadata.subsectionText
      }

      const newGroup: GroupedSource = {
        key,
        documentTitle,
        maxScore: source.score,
        quotes: [firstQuote],
      }
      // オプショナルプロパティは値がある場合のみ設定
      if (source.page !== undefined) {
        newGroup.page = source.page
      }
      // PDF物理ページ番号（APIから渡される）
      if (source.actualPdfPage !== undefined) {
        newGroup.actualPdfPage = source.actualPdfPage
      }
      if (source.metadata?.chapterText !== undefined) {
        newGroup.chapterText = source.metadata.chapterText
      }
      if (source.metadata?.sectionText !== undefined) {
        newGroup.sectionText = source.metadata.sectionText
      }
      if (source.metadata?.subsectionText !== undefined) {
        newGroup.subsectionText = source.metadata.subsectionText
      }
      if (source.metadata?.sourceUrl !== undefined) {
        newGroup.sourceUrl = source.metadata.sourceUrl
      }
      groupMap.set(key, newGroup)
    }
  }

  // Map から配列に変換
  const groups = Array.from(groupMap.values())

  // 各グループ内の引用をスコア降順でソート
  for (const group of groups) {
    group.quotes.sort((a, b) => b.score - a.score)
  }

  // グループ自体を最高スコア降順でソート
  groups.sort((a, b) => b.maxScore - a.maxScore)

  return groups
}
